/**
 * UI-4 Task 3 — TwilioProviderCard.
 *
 * The Twilio SMS channel admin card. Composes the Task 1 webhook-URL panel and
 * the Task 2 credentials form, and owns the connect / disconnect API calls.
 *
 * State (`useState`) is genuine UI state — `submitting`, `error`, `disconnectOpen`.
 * The credential FORM itself is Task 2's `TwilioCredentialsForm` (ZodForm-based,
 * per §0.F); this card is not a form.
 */
import { useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import TwilioWebhookUrlPanel from './TwilioWebhookUrlPanel.js'
import { TwilioCredentialsForm } from './TwilioCredentialsForm.js'
import DisconnectConfirmDialog from './DisconnectConfirmDialog.js'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'
import type { TwilioStatusResponse } from '../../../pages/admin/channels-types.js'
import { timestampMs } from '../../../utils/timezone.js'

export interface TwilioProviderCardProps {
  status: TwilioStatusResponse | null
  webhookUrl: string
  onChanged: () => void
}

interface TwilioCredentialsBody {
  accountSid: string
  authToken: string
  defaultFromHandle: string
}

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

/** Render an ISO timestamp as a coarse "x ago" relative string. */
function formatRelative(iso: string): string {
  const ts = timestampMs(iso)
  if (Number.isNaN(ts)) return 'unknown'
  const diffMs = Date.now() - ts
  if (diffMs < MINUTE_MS) return 'just now'
  if (diffMs < HOUR_MS) {
    const m = Math.floor(diffMs / MINUTE_MS)
    return `${m} minute${m === 1 ? '' : 's'} ago`
  }
  if (diffMs < DAY_MS) {
    const h = Math.floor(diffMs / HOUR_MS)
    return `${h} hour${h === 1 ? '' : 's'} ago`
  }
  const d = Math.floor(diffMs / DAY_MS)
  return `${d} day${d === 1 ? '' : 's'} ago`
}

export default function TwilioProviderCard(props: TwilioProviderCardProps): JSX.Element {
  const toast = useToast()
  const [submitting, setSubmitting] = useState(false)
  const [disconnectOpen, setDisconnectOpen] = useState(false)

  // `status === null` is the not-yet-fetched / loading state — treated as
  // not-configured so the card renders without crashing.
  const configured = props.status?.configured === true

  async function handleConnect(body: TwilioCredentialsBody): Promise<void> {
    setSubmitting(true)
    try {
      await api.post('/admin/channels/twilio/credentials', body)
      props.onChanged()
      toast.success('Twilio SMS connected')
    } catch (e) {
      toast.error('Failed to connect Twilio SMS', e instanceof Error ? e.message : 'Failed to connect Twilio SMS.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDisconnect(): Promise<void> {
    setSubmitting(true)
    try {
      await api.delete('/admin/channels/twilio/credentials')
      setDisconnectOpen(false)
      props.onChanged()
      toast.success('Twilio SMS disconnected')
    } catch (e) {
      setDisconnectOpen(false)
      toast.error('Failed to disconnect Twilio SMS', e instanceof Error ? e.message : 'Failed to disconnect Twilio SMS.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card title="Twilio SMS" className="w-full">
      <div className="space-y-3 text-sm">
        <div>
          {configured ? (
            <span className="rounded bg-green-50 px-2 py-1 text-xs font-medium text-green-700">
              ✓ Connected
            </span>
          ) : (
            <span className="rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
              Not connected
            </span>
          )}
        </div>

        <TwilioWebhookUrlPanel url={props.webhookUrl} />

        {configured && props.status !== null ? (
          <div className="space-y-2">
            <div>
              <span className="text-gray-500">From number: </span>
              <span className="font-medium">{props.status.fromHandle ?? '—'}</span>
            </div>
            {props.status.lastRotatedAt && (
              <div className="text-xs text-gray-600">
                Credentials last rotated {formatRelative(props.status.lastRotatedAt)}
              </div>
            )}
            <div className="pt-2">
              <Button
                label="Disconnect"
                onClick={() => setDisconnectOpen(true)}
                disabled={submitting}
                {...({ severity: 'danger' } as Record<string, unknown>)}
              />
            </div>
          </div>
        ) : (
          <TwilioCredentialsForm onSubmit={handleConnect} submitting={submitting} />
        )}

        <DisconnectConfirmDialog
          visible={disconnectOpen}
          displayName="Twilio SMS"
          pending={submitting}
          onCancel={() => setDisconnectOpen(false)}
          onConfirm={() => void handleDisconnect()}
        />
      </div>
    </Card>
  )
}
